/**
 * v294 — « Rétro hebdo » : page admin pour configurer l'envoi hebdomadaire
 * du compte rendu des techniciens (stats de la semaine + message IA adaptatif).
 *
 * UI minimale : toggle enabled, jour/heure d'envoi, seuil minimum de tickets
 * pour recevoir, prévisualisation par tech, envoi manuel immédiat.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ChartBar, PaperPlaneTilt, Eye, FloppyDisk, Calendar, Clock, Users,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const DAYS = [
  { v: 1, l: "Lundi" }, { v: 2, l: "Mardi" }, { v: 3, l: "Mercredi" },
  { v: 4, l: "Jeudi" }, { v: 5, l: "Vendredi" }, { v: 6, l: "Samedi" }, { v: 7, l: "Dimanche" },
];

export default function WeeklyDigestSettings() {
  const nav = useNavigate();
  const [settings, setSettings] = useState({ enabled: false, send_day: 1, send_hour: 8, min_tickets: 1 });
  const [techs, setTechs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewTech, setPreviewTech] = useState(null);
  const [previewStats, setPreviewStats] = useState(null);
  const [previewTone, setPreviewTone] = useState(null);
  const [selectedTech, setSelectedTech] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, u] = await Promise.all([
          api.get("/weekly-digest/settings"),
          api.get("/users?role=technicien&per_page=500"),
        ]);
        setSettings(s.data);
        const list = u.data.items || u.data || [];
        const only = list.filter((t) => t.role === "technicien" && t.active !== false);
        setTechs(only);
        if (only[0]) setSelectedTech(only[0].id);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur de chargement");
      }
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/weekly-digest/settings", settings);
      setSettings(r.data);
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const preview = async () => {
    if (!selectedTech) return toast.error("Choisissez un technicien");
    setBusy(true);
    try {
      const r = await api.get(`/weekly-digest/preview/${selectedTech}`);
      setPreviewHtml(r.data.html);
      setPreviewTech(r.data);
      setPreviewStats(r.data.stats);
      setPreviewTone(r.data.tone);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const sendNow = async (userId = null) => {
    if (!window.confirm(userId
      ? "Envoyer le compte rendu MAINTENANT à ce technicien ?"
      : "Envoyer le compte rendu MAINTENANT à TOUS les techs éligibles ?")) return;
    setBusy(true);
    try {
      const r = await api.post("/weekly-digest/trigger", userId ? { user_id: userId } : {});
      toast.success(`${r.data.sent} email(s) envoyé(s), ${(r.data.skipped || []).length} ignoré(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-5xl">
      <button onClick={() => nav("/parametres")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="wd-back">
        <ArrowLeft size={16}/> Paramètres
      </button>
      <div className="mb-6">
        <h1 className="text-3xl font-display font-bold mb-1 flex items-center gap-2">
          <ChartBar size={26}/> Rétro hebdo
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Chaque lundi matin, chaque technicien reçoit par email un compte rendu de sa semaine passée :
          tickets résolus, SLA, notes clients, verbatims — avec un message adaptatif généré par IA
          (chaleureux si top perf, bienveillant si semaine difficile). Comparaison anonyme à la médiane équipe.
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Calendar size={18}/> Planification</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Activer l'envoi automatique</Label>
              <p className="text-xs text-muted-foreground mt-1">Si désactivé, aucun email n'est envoyé par le cron (envoi manuel reste possible).</p>
            </div>
            <Switch
              checked={!!settings.enabled}
              onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
              data-testid="wd-enabled-toggle"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Jour d'envoi</Label>
              <Select value={String(settings.send_day)}
                      onValueChange={(v) => setSettings({ ...settings, send_day: parseInt(v, 10) })}>
                <SelectTrigger data-testid="wd-send-day"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAYS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Heure (Europe/Paris)</Label>
              <Input type="number" min={0} max={23} value={settings.send_hour}
                     onChange={(e) => setSettings({ ...settings, send_hour: parseInt(e.target.value || 0, 10) })}
                     data-testid="wd-send-hour"/>
            </div>
            <div>
              <Label>Seuil minimum (tickets résolus)</Label>
              <Input type="number" min={0} value={settings.min_tickets}
                     onChange={(e) => setSettings({ ...settings, min_tickets: parseInt(e.target.value || 0, 10) })}
                     data-testid="wd-min-tickets"/>
              <p className="text-[11px] text-muted-foreground mt-1">En dessous : le tech n'est pas spammé.</p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={busy} data-testid="wd-save">
              <FloppyDisk size={14} className="mr-1"/> {busy ? "…" : "Enregistrer"}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Note : le cron plateforme tourne chaque lundi 07:00 UTC. Les champs ci-dessus pilotent le filtre
            (opt-out par tech, seuil mini) ; pour changer vraiment le jour/l'heure du cron, modifiez <code>.emergent/crons.yml</code>.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Users size={18}/> Prévisualisation & envoi manuel</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Label>Technicien</Label>
              <Select value={selectedTech} onValueChange={setSelectedTech}>
                <SelectTrigger data-testid="wd-tech-select"><SelectValue placeholder="Choisir un technicien…"/></SelectTrigger>
                <SelectContent>
                  {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name} — {t.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={preview} disabled={busy || !selectedTech} data-testid="wd-preview-btn">
              <Eye size={14} className="mr-1"/> Prévisualiser
            </Button>
            <Button variant="outline" onClick={() => sendNow(selectedTech)} disabled={busy || !selectedTech} data-testid="wd-send-one-btn">
              <PaperPlaneTilt size={14} className="mr-1"/> Envoyer à ce tech
            </Button>
            <Button onClick={() => sendNow()} disabled={busy} data-testid="wd-send-all-btn">
              <PaperPlaneTilt size={14} className="mr-1"/> Envoyer à tous
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            L'envoi manuel ne tient pas compte du toggle « Activer l'envoi automatique » (il force l'envoi).
            Il respecte toujours l'opt-out individuel et le seuil minimum.
          </p>
        </CardContent>
      </Card>

      {/* Preview modal */}
      <Dialog open={!!previewHtml} onOpenChange={(v) => !v && setPreviewHtml(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={18}/> Aperçu — {previewTech?.full_name}
              {previewTone && <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">ton : {previewTone}</span>}
            </DialogTitle>
            <DialogDescription>
              Rendu exact qu'il recevrait par email. Aucun envoi effectué.
              {previewStats && <> · {previewStats.tickets_resolved_count} tickets résolus · note {previewStats.avg_satisfaction || "—"}/5</>}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto border rounded">
            <iframe
              srcDoc={previewHtml}
              title="Preview compte rendu"
              className="w-full h-[65vh] border-0 bg-white"
              data-testid="wd-preview-iframe"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
